import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Mail } from 'lucide-react';
import axios from 'axios';
import { API, GOOGLE_CLIENT_ID } from '@/config';

export default function AuthPage({ mode = 'login' }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const isSignup = mode === 'signup';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || (isSignup && !name)) {
      toast.error('Please fill all fields');
      return;
    }
    setSubmitting(true);
    try {
      if (isSignup) {
        const res = await axios.post(`${API}/auth/signup`, { email, password, name });
        if (res.data.needs_verification) {
          setVerificationSent(true);
          toast.success('Check your email for verification link');
        }
      } else {
        await login(email, password);
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong';
      if (err.response?.status === 403 && detail.includes('not verified')) {
        setVerificationSent(true);
        toast.info('Verification email sent. Check your inbox.');
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email, password: password || 'dummy' });
      toast.success('Verification email resent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Verification Sent Screen ───
  if (verificationSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm text-center" data-testid="verification-sent">
          <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-6">
            <Mail className="h-8 w-8 text-[#10B981]" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Check your email</h1>
          <p className="text-sm text-muted-foreground mb-2">
            We've sent a verification link to:
          </p>
          <p className="text-sm font-semibold text-foreground mb-6">{email}</p>
          <p className="text-xs text-muted-foreground mb-6">
            Click the link in the email to verify your address and activate your account. The link expires in 24 hours.
          </p>
          <div className="space-y-3">
            <Button variant="outline" className="w-full" onClick={handleResend} disabled={submitting} data-testid="resend-btn">
              {submitting ? 'Sending...' : 'Resend Verification Email'}
            </Button>
            <Link to="/login">
              <Button variant="ghost" className="w-full" data-testid="back-to-login-btn">
                Back to Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-8 h-8 rounded-lg bg-[#10B981] flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="10" width="3" height="8" rx="1.5" fill="white" opacity="0.7" />
              <rect x="8" y="6" width="3" height="16" rx="1.5" fill="white" />
              <rect x="13" y="3" width="3" height="18" rx="1.5" fill="white" />
              <rect x="18" y="8" width="3" height="10" rx="1.5" fill="white" opacity="0.7" />
            </svg>
          </div>
          <span className="text-lg font-bold tracking-tight text-foreground">Whisper.AI</span>
        </div>

        <h1 className="text-2xl font-bold text-foreground mb-1" data-testid="auth-title">
          {isSignup ? 'Create your account' : 'Welcome back'}
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          {isSignup ? 'Start building your AI chat widget' : 'Sign in to your dashboard'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
          {isSignup && (
            <div className="space-y-1.5">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="auth-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              {!isSignup && (
                <Link to="/forgot-password" className="text-xs text-[#10B981] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              )}
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                className="pr-10"
                data-testid="auth-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={submitting} data-testid="auth-submit-btn">
            {submitting ? 'Loading...' : isSignup ? 'Create Account' : 'Sign In'}
          </Button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
          <div className="relative flex justify-center text-xs"><span className="bg-background px-3 text-muted-foreground">or</span></div>
        </div>

        <div className="flex justify-center" data-testid="google-signin-btn">
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => {
              const redirectUri = window.location.origin + '/auth/google/callback';
              const scope = 'openid email profile';
              const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=select_account`;
              window.location.href = url;
            }}
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </Button>
        </div>

        <p className="text-sm text-center text-muted-foreground mt-6">
          {isSignup ? (
            <>Already have an account? <Link to="/login" className="text-[#10B981] hover:underline" data-testid="switch-to-login">Sign in</Link></>
          ) : (
            <>Don't have an account? <Link to="/signup" className="text-[#10B981] hover:underline" data-testid="switch-to-signup">Sign up</Link></>
          )}
        </p>
      </div>
    </div>
  );
}
